<template>
    <div class="map-wrapper">
        <div ref="map" id="ol-map-container" class="map-container"></div>
        <div class="map-bottom" v-if="vessel">
            <div class="warm-alert">
                温馨提示：装卸货港位置和船位信息仅供参考，如发现与实际情况不一致，请及时联系船货不二业务员
            </div>
            <div class="info-panel">
                <div class="line">
                    <div class="label">承运船舶</div>
                    <div class="content">{{vessel.vesselName}}<span class="info-mmsi">（MMSI：{{vessel.mmsi}}）</span>
                    </div>
                </div>
                <div class="line" v-if="vessel.aisInfo">
                    <div class="label">AIS信息</div>
                    <div class="content">{{vessel.aisInfo}}</div>
                </div>
                <div class="line">
                    <div class="label">货物信息</div>
                    <div class="content">
                        <span class="d-inline-block">{{vessel.startText}}</span>
                        &nbsp;→&nbsp;<span class="d-inline-block">{{vessel.targetText}}</span>
                        &nbsp;<span class="d-inline-block">{{vessel.cargoName}}</span>
                        &nbsp;<span class="d-inline-block">{{vessel.cargoWeight}}</span>
                    </div>
                </div>
            </div>
        </div>
        <div class="map-top">
            <img src="../assets/logo.png" class="logo" alt="logo"/>
            <div class="ml-2 title">船货不二承运船舶跟踪管理系统</div>
        </div>
        <div v-if="mask" class="map-mask">
            <div class="content">
                本次运输已结束<br>如有问题请联系船货不二业务员
            </div>
        </div>
    </div>
</template>

<script>
import * as moment from "moment";
import * as storage from "@/utils/storage";
import OpenLayersMap from 'ol/Map';
import {defaults as defaultInteractions} from "ol/interaction";
import {defaults as defaultControls} from "ol/control";
import ScaleLineControl from "@/olMap/control/ScaleLine";
import MapTypeControl from "@/olMap/control/MapType";
import View from "ol/View";
import {getLayers} from "@/olMap/mapLayers";
import {fromLonLat} from "@/olMap/coordUtils";
import VectorLayer from "ol/layer/Vector";
import VectorSource from "ol/source/Vector";
import loadingVessel from "@/services/loadingVessel";
import * as olExtent from "ol/extent";
import {
    createFeature, createLabelLineStyle, createLabelStyle,
    createVesselStyle, getLabelItem, getLabelRect, labelDirections, trailFeatures
} from "@/olMap/featureUtils";
import {Icon, Style} from "ol/style";

const anonIdKey = "chb3.loadingVesselMap.anonId";

export default {
    name: "LoadingVesselMap",
    data() {
        return {
            mask: false,
            vessel: null,
            trail: null,
        };
    },
    methods: {
        updateLayers() {
            if (this.vessel) {
                this.updatePortLayer();
                let occupiedRects = [];
                this.updateVesselLayer(occupiedRects);
                this.updateTrailLayer(occupiedRects);
            } else {
                this.portLayer.setSource(null);
                this.trailLayer.setSource(null);
                this.vesselLayer.setSource(null);
            }
        },
        updatePortLayer() {
            let features = [];
            this.getPlots().forEach(p => {
                if (p.isStart) {
                    features.push(this.createPortFeature(p, "image/marker_load_sprite.png"));
                } else if (p.isTarget) {
                    features.push(this.createPortFeature(p, "image/marker_unload_sprite.png"));
                }
            });
            this.portLayer.setSource(new VectorSource({features}));
        },
        updateVesselLayer(occupiedRects) {
            let features = [];
            let location = this.trail && this.trail[0] || this.vessel && this.vessel.location;
            if (this.vessel && location) {
                let coordinate = location.coordinate;
                let vessel = Object.assign({}, this.vessel, location);
                let style = createVesselStyle(vessel, true, null);
                let feature = createFeature(coordinate, style, {vessel: this.vessel});
                features.push(feature);

                let text = this.vessel.displayName;
                let pixel = this.map.getPixelFromCoordinate(coordinate);
                if (pixel) {
                    occupiedRects.push([pixel[0] - 17, pixel[1] - 17, pixel[0] + 17, pixel[1] + 17]);
                }
                let direction = labelDirections[0];
                features.push(createFeature(coordinate, createLabelLineStyle(direction.angle, true, false)));
                features.push(createFeature(coordinate, createLabelStyle({text, direction}, true, false)));
                let labelItem = getLabelItem(Object.assign({}, this.vessel, {coordinate}), true);
                if (labelItem) {
                    occupiedRects.push(getLabelRect(labelItem, direction, this.map));
                }
            }
            this.vesselLayer.setSource(new VectorSource({features}));
        },
        updateTrailLayer(occupiedRects) {
            let features = [];
            if (this.trail && this.trail[0]) {
                let resolution = this.map.getView().getResolution();
                features = features.concat(trailFeatures(this.trail, resolution, this.trail[0], occupiedRects, this.map));
            }
            this.trailLayer.setSource(new VectorSource({features}));
        },
        createPortFeature(p, imagePath) {
            return createFeature(p.coordinate, new Style({
                image: new Icon({src: imagePath, size: [40, 40], anchor: [0.5, 0.9]})
            }), {plot: p, tooltip: p.name, type: 'port'});
        },
        updateViewPort() {
            let coordinates = [];
            if (this.map) {
                if (this.$refs.map) {
                    let size = this.getClientSize();
                    this.map.setSize(size);
                }
                coordinates = this.getPlots().map(x => x.coordinate);
                if (this.trail && this.trail[0]) {
                    coordinates.push(this.trail[0].coordinate);
                } else if (this.vessel && this.vessel.location) {
                    coordinates.push(this.vessel.location.coordinate);
                }
            }
            if (coordinates.length > 0) {
                let size = this.getClientSize();
                let px = Math.max(20, Math.round(size[0] * 0.2));
                let py = Math.max(20, Math.round(size[1] * 0.2));
                this.map.getView().fit(olExtent.boundingExtent(coordinates), {
                    padding: [py + 100, px, py, px],
                    maxZoom: 13,
                });
            }
            this.updateLayers();
        },
        getClientSize() {
            return [this.$refs.map.clientWidth, this.$refs.map.clientHeight];
        },
        getPlots() {
            if (this.vessel && this.vessel.route && Array.isArray(this.vessel.route.plots)) {
                return this.vessel.route.plots;
            }
            return [];
        },
        updateVesselInfo(location) {
            let time = moment(location.updateTime || location.time);
            let seconds = Math.max(0, moment().diff(time, "second"));
            this.vessel.aisInfo = this.getAisInfo(location, seconds);
            this.vessel.recent = seconds < 2 * 3600;
            this.vessel.in24h = seconds < 24 * 3600;
            this.vessel.displayName = this.vessel.vesselName;
            this.vessel.senior = true;
        },
        getAisInfo(location, seconds) {
            if (location) {
                let desc = "刚刚";
                let minutes = Math.floor(seconds / 60);
                if (minutes > 0) {
                    let hours = Math.floor(minutes / 60);
                    if (hours > 0) {
                        desc = hours + "小时前";
                    } else {
                        desc = minutes + "分钟前";
                    }
                }
                let sog = +(+location.sog || 0).toFixed(2);
                return `${desc}更新，航速${sog}节`;
            }
            return "";
        },
        patchCoordinate(x) {
            if (x) {
                x.coordinate = fromLonLat([x.lon, x.lat]);
            }
        },
    },
    created() {
        // let ua = window.navigator.userAgent;
        // if (/micromessenger/.test(ua.toLowerCase())) {
        //     let hash = this.$route.query["hash"];
        //     let code = this.$route.query["code"];
        //     let test = this.$route.query["test"];
        //     if (hash && !code && !test) {
        //         let pathStr = "https://biz.chb2.com" + this.$route.path;
        //         this.redirecting = true;
        //         window.location.href = "https://open.weixin.qq.com/connect/oauth2/authorize" +
        //             "?appid=wxf4587334a31396b7&redirect_uri=" + encodeURI(pathStr) +
        //             "&response_type=code&scope=snsapi_base&state=" + hash + "#wechat_redirect";
        //     }
        // }
    },
    async mounted() {
        this.portLayer = new VectorLayer({source: new VectorSource({features: []})});
        this.trailLayer = new VectorLayer({source: new VectorSource({features: []})});
        this.vesselLayer = new VectorLayer({source: new VectorSource({features: []})});
        this.map = new OpenLayersMap({
            target: 'ol-map-container',
            interactions: defaultInteractions({
                altShiftDragRotate: false,
                pinchRotate: false,
            }),
            controls: defaultControls().extend([
                new ScaleLineControl({units: 'metric'}),
                new MapTypeControl({mapType: 'satellite'}),
            ]),
            loadTilesWhileInteracting: true,
            view: new View({center: fromLonLat([119.511392, 32.21177]), zoom: 11, maxZoom: 19}),
            layers: getLayers("gaode").concat([
                this.trailLayer,
                this.portLayer,
                this.vesselLayer,
            ]),
        });

        new ResizeObserver(() => this.updateViewPort()).observe(this.$refs.map);
        this.map.on("moveend", () => this.updateLayers());

        let hash = this.$route.query["hash"] || this.$route.query["state"];
        let code = this.$route.query["code"] || "";
        if (hash && !this.redirecting) {
            let anonId = storage.get(anonIdKey) || "";
            let vessel = await loadingVessel.getByHash(hash, code, anonId);
            if (vessel && vessel.anonId && vessel.anonId !== anonId) {
                storage.set(anonIdKey, vessel.anonId);
            }
            if (vessel && vessel.id) {
                vessel.startText = vessel.startPorts.join("，");
                vessel.targetText = vessel.targetPorts.join("，");
                vessel.cargoWeight = +vessel.cargoWeight ? (+vessel.cargoWeight + "吨") : "";
                vessel.cargoName = vessel.cargoName || "";
                this.mask = !!vessel.over;
                this.vessel = vessel;
                this.patchCoordinate(this.vessel.location);
                this.trail = vessel.trail;
                if (this.trail && this.trail[0]) {
                    this.trail.forEach(x => this.patchCoordinate(x));
                    this.updateVesselInfo(this.trail[0]);
                } else {
                    this.updateVesselInfo(this.vessel.location);
                }
                this.getPlots().forEach(x => this.patchCoordinate(x));
                this.updateViewPort();
            } else {
                this.mask = true;
            }
        }
    },
}
</script>

<style scoped>
.map-wrapper {
    position: relative;
    width: 100%;
    height: 100vh;
    height: 100dvh;
    display: flex;
    flex-direction: column;
}

.map-container {
    width: 100%;
    flex: 1;
}

.map-top {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    padding: 5px 10px;
    background: rgba(255, 255, 255, 0.75);
    display: flex;
    flex-direction: row;
    align-items: center;
}

.map-top > .logo {
    width: 40px;
    height: 40px;
    display: block;
    box-sizing: border-box;
}

.map-top > .title {
    font-size: 18px;
    font-weight: bold;
    color: #37A1E8;
}

.map-bottom {
    width: 100%;
}

.map-bottom > .warm-alert {
    padding: 3px 10px;
    font-size: 14px;
    background: #FCF7D4;
    color: #F79D06;
}

.map-bottom > .info-panel {
    background: #F3F4FD;
    padding: 5px 10px 10px 10px;
}

.map-bottom > .info-panel > .line {
    display: flex;
    font-size: 16px;
}

.map-bottom > .info-panel > .line > .label {
    width: 70px;
    text-align: right;
    color: dimgray;
}

.map-bottom > .info-panel > .line > .content {
    margin-left: 1rem;
    color: black;
    flex: 1;
    text-align: left;
}

.info-mmsi {
    font-size: 15px;
}

.map-mask {
    position: absolute;
    left: 0;
    top: 0;
    width: 100%;
    height: 100%;
    background: rgba(255, 255, 255, 0.9);
    display: flex;
    flex-direction: column;
    place-items: center;
    justify-content: space-around;
}

.map-mask > .content {
    display: flex;
    flex-direction: row;
    place-items: center;
    justify-content: space-around;
    text-align: center;
    color: darkorange;
    font-size: 18px;
    font-weight: bold;
}
</style>

<style>
.ol-map-type {
    top: 60px;
    left: 10px;
}

.ol-control.ol-map-type {
    display: flex;
}

.ol-control.ol-map-type button {
    width: auto;
    height: auto;
    line-height: normal;
    font-size: 18px;
    font-weight: normal;
    padding: 5px 10px;
}

.ol-control.ol-map-type button.selected {
    text-decoration: none;
    background-color: rgba(0, 60, 136, .8);
}

.ol-scale-line {
    left: auto;
    top: 60px;
    right: 50px;
    bottom: auto;
}

.ol-zoom {
    left: auto;
    top: 60px;
    right: 10px;
    bottom: auto;
}
</style>
